import { useState, useEffect, useCallback } from 'react';
import { Autocomplete, TextField, CircularProgress, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  getCountries,
  getStatesOfCountry,
  getCitiesOfState,
} from '@countrystatecity/countries-browser';

// Minimal shape — only what we actually need
interface LocationOption {
  name: string;
  code: string;  // iso2 for countries and states; city name for cities (no code needed)
}

interface Props {
  onChange: (country: string, governorate: string, city: string) => void;
  errors?: { governorate?: string; city?: string };
}

export default function CascadingDropdown({ onChange, errors }: Props) {
  const { t } = useTranslation();

  const [countries, setCountries]     = useState<LocationOption[]>([]);
  const [states, setStates]           = useState<LocationOption[]>([]);
  const [cities, setCities]           = useState<string[]>([]);

  const [country, setCountry]         = useState<LocationOption | null>(null);
  const [state, setState]             = useState<LocationOption | null>(null);
  const [city, setCity]               = useState<string | null>(null);

  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // ── Load country list on mount ─────────────────────────────────────────────
  useEffect(() => {
    getCountries().then((list) => {
      // Map to minimal { name, code } — discard latitude, id, etc.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const options: LocationOption[] = (list as any[]).map((c) => ({
        name: c.name as string,
        code: (c.iso2 ?? c.isoCode ?? '') as string,
      }));
      setCountries(options);
      // Default to Egypt
      const egypt = options.find((c) => c.code === 'EG') ?? null;
      setCountry(egypt);
    });
  }, []);

  // ── Load states when country changes ──────────────────────────────────────
  useEffect(() => {
    if (!country) { setStates([]); setState(null); setCities([]); setCity(null); return; }
    setLoadingStates(true);
    getStatesOfCountry(country.code).then((list) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const options: LocationOption[] = (list as any[]).map((s) => ({
        name: s.name as string,
        code: (s.iso2 ?? s.isoCode ?? '') as string,
      }));
      setStates(options);
      setState(null);
      setCities([]);
      setCity(null);
      setLoadingStates(false);
    });
  }, [country]);

  // ── Load cities when state changes ────────────────────────────────────────
  useEffect(() => {
    if (!country || !state) { setCities([]); setCity(null); return; }
    setLoadingCities(true);
    getCitiesOfState(country.code, state.code).then((list) => {
      // Cities are just names — no code needed
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const names: string[] = (list as any[]).map((c) => c.name as string);
      setCities(names);
      setCity(null);
      setLoadingCities(false);
    });
  }, [country, state]);

  // ── Notify parent ─────────────────────────────────────────────────────────
  const notifyParent = useCallback(() => {
    onChange(country?.name ?? '', state?.name ?? '', city ?? '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, state, city]);

  useEffect(() => { notifyParent(); }, [notifyParent]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Country */}
      <Autocomplete
        options={countries}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a.code === b.code}
        value={country}
        onChange={(_, v) => setCountry(v)}
        renderInput={(params) => (
          <TextField {...params} label={t('register.country')} />
        )}
      />

      {/* Governorate / State */}
      <Autocomplete
        options={states}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a.code === b.code}
        value={state}
        onChange={(_, v) => setState(v)}
        loading={loadingStates}
        disabled={!country}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('register.governorate')}
            error={!!errors?.governorate}
            helperText={errors?.governorate}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingStates && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {/* City */}
      <Autocomplete
        options={cities}
        value={city}
        onChange={(_, v) => setCity(v)}
        loading={loadingCities}
        disabled={!state}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('register.city')}
            error={!!errors?.city}
            helperText={errors?.city}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingCities && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}
